import { useMemo, useState } from "react";
import { t } from "@/i18n-js/instance";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { RadioButtonGroup } from "@circle-react-shared/RadioButtonGroup";
import { Typography } from "@circle-react-uikit/Typography";
import { useEventsContext } from "../../EditEventContext";
import { RECURRING_EDIT_MODE } from "../../utils";

const translationRoot = "events.edit.recurring_event_modal";

interface EditRecurringModalProps {
  isPublishing?: boolean;
  onCloseModal: () => void;
  onConfirm: (frequency: string) => void;
  shouldHideAllEventOption?: boolean;
  show?: boolean;
  showCurrentOptionOnly?: boolean;
}

interface RadioOption {
  label: string;
  value: string;
}

export const EditRecurringModal = ({
  show = false,
  onConfirm,
  onCloseModal,
  isPublishing = false,
  showCurrentOptionOnly,
  shouldHideAllEventOption,
}: EditRecurringModalProps) => {
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    RECURRING_EDIT_MODE.CURRENT,
  );
  const { isEmailNotificationsEnabled } = useEventsContext();

  const onRadioChangeHandler = (value: string): void => {
    setSelectedFrequency(value);
  };

  const onConfirmHandler = (): void => {
    onConfirm(selectedFrequency);
  };

  const defaultOptions: RadioOption[] = [
    {
      label: t(`${translationRoot}.this_event`),
      value: RECURRING_EDIT_MODE.CURRENT,
    },
    {
      label: t(`${translationRoot}.this_and_following`),
      value: RECURRING_EDIT_MODE.REMAINING,
    },
    {
      label: t(`${translationRoot}.all_events`),
      value: RECURRING_EDIT_MODE.ALL,
    },
  ];

  const areMultipleEvents =
    selectedFrequency === RECURRING_EDIT_MODE.ALL ||
    selectedFrequency === RECURRING_EDIT_MODE.REMAINING;

  const futureOptionsOnly = defaultOptions.filter(
    option => option.value !== RECURRING_EDIT_MODE.ALL,
  );

  const currentOption = defaultOptions.filter(
    option => option.value === RECURRING_EDIT_MODE.CURRENT,
  );

  const menuOptions = useMemo<RadioOption[]>(() => {
    let options = defaultOptions;

    if (showCurrentOptionOnly) {
      options = currentOption;
    } else if (shouldHideAllEventOption) {
      options = futureOptionsOnly;
    }

    return options;
  }, [showCurrentOptionOnly, shouldHideAllEventOption]);

  const shouldShowEmailNotificationsMessage =
    isEmailNotificationsEnabled && areMultipleEvents && isPublishing;

  return (
    <ConfirmationModal
      title={
        isPublishing
          ? t(`${translationRoot}.title_publish`)
          : t(`${translationRoot}.title`)
      }
      isOpen={show}
      confirmText={t(`${translationRoot}.accept`)}
      cancelText={t(`${translationRoot}.cancel`)}
      onClose={onCloseModal}
      onCancel={onCloseModal}
      onConfirm={onConfirmHandler}
      size="sm"
    >
      <div className="mt-6">
        <RadioButtonGroup
          defaultChecked={RECURRING_EDIT_MODE.CURRENT}
          name="recurring_event_settings.frequency_edit"
          options={menuOptions}
          size="sm"
          onChange={onRadioChangeHandler}
        />
      </div>
      {shouldShowEmailNotificationsMessage && (
        <div className="mt-4 leading-4">
          <Typography.LabelXs color="text-light" weight="semibold" as="span">
            {t(`${translationRoot}.email_notifications`)}
          </Typography.LabelXs>
        </div>
      )}
    </ConfirmationModal>
  );
};
